import type {
  BlockOutput,
  ClarifyBlockData,
  ClarifyFormBlockData,
} from '../../types';

// Recover the values a user submitted for a clarify form from the answer
// message they sent. `buildSubmitMessage` appends a `<ctx:fields=key=value;
// ...>` marker to that message, so the machine values are always in the
// transcript — this re-reads them at hydration so the consumed (read-only)
// form shows what was submitted instead of the empty original defaults.

// Pull `key=value` pairs out of the first `<ctx:…>` marker. Segments are
// `;`-separated; the first may carry a `fields=` prefix, and the marker can
// also hold agent-only constants (job_id, etc.) — the caller keeps only the
// keys that match a form field.
export const parseSubmittedFields = (
  content: string
): Record<string, string> => {
  const start = content.indexOf('<ctx:');
  if (start < 0) return {};
  const end = content.indexOf('>', start);
  if (end < 0) return {};
  const inner = content.slice(start + '<ctx:'.length, end);
  const out: Record<string, string> = {};
  for (const raw of inner.split(';')) {
    const seg = raw.trim().replace(/^fields=/, '');
    const eq = seg.indexOf('=');
    if (eq < 0) continue;
    const key = seg.slice(0, eq).trim();
    const value = seg.slice(eq + 1).trim();
    if (key) out[key] = value;
  }
  return out;
};

// Seed a consumed clarify form's fields with the submitted answers: text
// fields take the value as `prefill`, pickers as `selected` (multi values stay
// comma-joined — `buildDefaults` splits them). Fields with no submitted value
// are left as-is.
const injectClarifyFormAnswers = (
  data: ClarifyFormBlockData,
  answers: Record<string, string>
): ClarifyFormBlockData => {
  if (Object.keys(answers).length === 0) return data;
  const actions = data.actions.map((field) => {
    const value = answers[field.key];
    if (value === undefined) return field;
    return field.type === 'picker'
      ? { ...field, selected: value }
      : { ...field, prefill: value };
  });
  return { ...data, actions };
};

// Recover the option a single-picker clarify user chose. Its submit fills the
// pending template with `${value} (${label})` (no ctx marker), so the picked
// option is the one whose `value (label)` fragment appears in the answer.
const recoverClarifyChoice = (
  data: ClarifyBlockData,
  answer: string
): string | undefined =>
  data.options.find((o) => answer.includes(`${o.value} (${o.label})`))?.value;

// Mark a clarify/clarifyForm block consumed, and backfill the answer the user
// gave from the triggering message: form fields for clarifyForm, the picked
// option for the single-question clarify picker.
export const consumeClarifyBlock = (
  block: BlockOutput,
  answerContent: string
): BlockOutput => {
  if (block.type === 'clarifyForm') {
    const answers = parseSubmittedFields(answerContent);
    const withAnswers = injectClarifyFormAnswers(block.data, answers);
    return { ...block, data: { ...withAnswers, consumed: true } };
  }
  if (block.type === 'clarify') {
    return {
      ...block,
      data: {
        ...block.data,
        consumed: true,
        answered: recoverClarifyChoice(block.data, answerContent),
      },
    };
  }
  return block;
};
